import { ICommandBarItemProps } from "@fluentui/react";

export interface AddSubscriptionCommandStrings {
    AddSubscription?: string;
    AddSubscriptionManual?: string;
    AddSubscriptionOnline?: string;
    MoreOptions?: string;
}

export interface AddSubscriptionCommandOptions {
    isGlobalAdmin: boolean;
    stripeEnabled: boolean;
    hasSelectedTenant: boolean;
    isBusy: boolean;
    /** Tenant-specific Online URL from resolveOnlineSubscriptionUrl; null/undefined hides Online. */
    onlineUrl: string | null | undefined;
    strings?: AddSubscriptionCommandStrings | null;
    /** Opens the AddSubscriptionPanel (the "Manual" path). */
    onManual: () => void;
    /** Opens the Online URL. */
    onOnline: () => void;
}

type ItemClickHandler = NonNullable<ICommandBarItemProps["onClick"]>;

// Fluent's ContextualMenuSplitButton (how a split item renders once the toolbar overflows into the
// "..." menu) invokes item.onClick twice per click: once as the inner primary <button>'s native
// onClick and once via executeItemClick on the surrounding container. Both calls carry the same
// native event, so remembering the events an action has already handled collapses them into one
// action, while distinct clicks (and keyboard activations, which synthesise a fresh click) still
// each act once. The set is per wrapped action and weak, so it dies with the command it belongs to.
const once = (action: () => void): ItemClickHandler => {
    const handledEvents = new WeakSet<Event>();
    return (ev) => {
        const nativeEvent = ev?.nativeEvent;
        if (nativeEvent) {
            if (handledEvents.has(nativeEvent)) return;
            handledEvents.add(nativeEvent);
        }
        action();
    };
};

/**
 * Builds the toolbar's "Add subscription" command, or null when it must not be shown.
 *
 * | User             | Online URL configured and Stripe enabled              | Otherwise                   |
 * |------------------|-------------------------------------------------------|-----------------------------|
 * | Global admin     | Split button: primary = Manual, menu = Manual / Online | Plain button opening Manual |
 * | Non-global admin | Plain button opening Online                           | No button                   |
 *
 * Online needs Stripe because a purchase made on the public page only reaches Docubird through
 * the Stripe webhooks. The AddSubscriptionPanel stays global-admin only; the backend
 * PortalAdminPolicy on /api/portal/subscriptions/create remains the authoritative gate.
 */
export const buildAddSubscriptionCommand = (options: AddSubscriptionCommandOptions): ICommandBarItemProps | null => {
    const { isGlobalAdmin, stripeEnabled, hasSelectedTenant, isBusy, onlineUrl, strings, onManual, onOnline } = options;
    if (!hasSelectedTenant) return null;

    const onlineAvailable = stripeEnabled && !!onlineUrl;
    const openManual = once(onManual);
    const openOnline = once(onOnline);

    const button: ICommandBarItemProps = {
        key: "add",
        text: strings?.AddSubscription || "Add subscription",
        iconProps: { iconName: "Add" },
        disabled: isBusy,
    };

    if (!isGlobalAdmin) {
        return onlineAvailable ? { ...button, onClick: openOnline } : null;
    }

    if (!onlineAvailable) {
        return { ...button, onClick: openManual };
    }

    // Fluent renders a split button only when `split`, `onClick` AND `subMenuProps` are all set:
    // the primary half fires onClick, the chevron opens the submenu. CommandBar spreads the item
    // onto the CommandBarButton, so splitButtonAriaLabel (the chevron's accessible name) passes
    // through even though it is not a declared IContextualMenuItem prop.
    return {
        ...button,
        split: true,
        // CommandBar's ResizeGroup caches item measurements by `cacheKey ?? key`. The split variant
        // is wider than the plain one, so it needs its own key or a plain->split flip under an
        // otherwise unchanged toolbar would reuse stale widths when deciding what overflows.
        cacheKey: "add-split",
        splitButtonAriaLabel: strings?.MoreOptions || "More options",
        onClick: openManual,
        subMenuProps: {
            items: [
                {
                    key: "add-manual",
                    text: strings?.AddSubscriptionManual || "Manual",
                    iconProps: { iconName: "EditNote" },
                    onClick: openManual,
                },
                {
                    key: "add-online",
                    text: strings?.AddSubscriptionOnline || "Online",
                    iconProps: { iconName: "OpenInNewTab" },
                    onClick: openOnline,
                },
            ],
        },
    };
};

/**
 * The public "Online" add-subscription page, configured per environment in the portal's
 * .env.development / .env.production files. Undefined when unset or blank, which hides the
 * Online option everywhere.
 *
 * Read at call time (not module load) and referenced as the full static expression so CRA's
 * build-time inlining of REACT_APP_* variables still applies.
 */
export const getOnlineSubscriptionBaseUrl = (): string | undefined => {
    const value = process.env.REACT_APP_ONLINE_SUBSCRIPTION_URL?.trim();
    return value ? value : undefined;
};

// Only web URLs may be opened. `new URL` happily parses javascript:, data:, file: and mailto:
// values and window.open would act on them; a misconfigured env var must not become that.
const ALLOWED_PROTOCOLS = new Set(["http:", "https:"]);

// Canonical 8-4-4-4-12 form (.NET "D" format): what the backend's payment-link resolver accepts in
// client_reference_id via Guid.TryParse. It also refuses the empty GUID, so we do too, rather than
// sending a purchase down a path that can only end as an unresolvable-tenant alert.
const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

/**
 * Builds the tenant-specific "Online" add-subscription URL from the configured base URL by
 * appending (or replacing) a `tenantGuid` query parameter. Any query string or hash already on
 * the configured URL is preserved.
 *
 * Returns null, which callers treat as "Online mode is unavailable", when either input is blank,
 * the base URL is not an absolute http(s) URL, or the tenant guid is not a well-formed non-empty
 * GUID.
 */
export const resolveOnlineSubscriptionUrl = (
    baseUrl: string | null | undefined,
    tenantGuid: string | null | undefined
): string | null => {
    const trimmedBase = baseUrl?.trim();
    const normalizedGuid = tenantGuid?.trim().toLowerCase();
    if (!trimmedBase || !normalizedGuid) return null;
    if (!GUID_PATTERN.test(normalizedGuid) || normalizedGuid === EMPTY_GUID) return null;

    let url: URL;
    try {
        url = new URL(trimmedBase);
    } catch {
        return null;
    }
    if (!ALLOWED_PROTOCOLS.has(url.protocol)) return null;

    url.searchParams.set("tenantGuid", normalizedGuid);
    return url.toString();
};
